// ═══════════════════════════════════════════════════════════════════════════════
// Employer subscription page (§22.9.6)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  SUBSCRIPTION_BILLING_INTERVAL,
  SUBSCRIPTION_STATUS,
  USER_LEVEL,
  addBillingPeriodUtc,
  applySubscriptionUpgradeAmountTemplate,
  applySubscriptionUpgradeTemplate,
  ensureUpgradeCopyPeriodPlaceholder,
  parseUtcDatetimeToDate,
  resolveSubscriptionUpgradeCopy,
  type SubscriptionBillingInterval,
  type SubscriptionInvoiceItem,
  type SubscriptionPlanQuote,
  type SubscriptionPreview,
  type SubscriptionAppliedPromo,
  type SubscriptionStatusSummary,
} from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { subscriptionApi } from '../../lib/api';
import { usePublicConfig } from '../../hooks/usePublicConfig';
import {
  formatUtcForUserTimezone,
  mapLanguageToLocale,
} from '../../lib/formatUtcDatetime';
import { navigateBack } from '../../lib/navigateBack';
import { detectReturnedFromStripe, markStripeCheckoutPending } from '../../lib/stripeNavigation';
import { canUseStripeBillingUi, isPlayStoreClient } from '../../lib/clientChannel';
import { RedeemInvitationCodeForm } from '../../components/settings/RedeemInvitationCodeForm';
import { MembershipPricingCompareModal } from '../../components/membership/MembershipPricingCompareModal';
import {
  buildSubscriptionTermsConsentParts,
  SubscriptionTermsModal,
} from '../../components/subscription/SubscriptionTermsModal';
import { AppButton } from '../../components/ui/AppButton';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../components/ui/ToastProvider';

type SubscriptionStatusResponse = SubscriptionStatusSummary & {
  stripeConfigured: boolean;
};

const levelLabelKey = (level: number): string => {
  if (level === USER_LEVEL.PAID) {
    return 'usage.levelPaid';
  }
  if (level === USER_LEVEL.FREE) {
    return 'usage.levelFree';
  }
  return 'usage.levelTrial';
};

const formatMoney = (currency: string, amountCents: number): string => {
  const value = (amountCents / 100).toFixed(2);
  return `${currency} ${value}`;
};

const formatMoneyCompact = (currency: string, amountCents: number): string => {
  const value = (amountCents / 100).toFixed(2);
  return `${currency.toUpperCase()}$${value}`;
};

const formatUtcDatetimeFromDate = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

export const SubscriptionPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { refreshUser, user } = useAuth();
  const { showSuccessToast, showErrorToast } = useToast();

  const [status, setStatus] = useState<SubscriptionStatusResponse | null>(null);
  const [preview, setPreview] = useState<SubscriptionPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [intervalBusy, setIntervalBusy] = useState(false);
  const [pendingIntervalConfirm, setPendingIntervalConfirm] =
    useState<SubscriptionBillingInterval | null>(null);
  const [cancelIntervalConfirmOpen, setCancelIntervalConfirmOpen] = useState(false);
  const [selectedInterval, setSelectedInterval] =
    useState<SubscriptionBillingInterval>(SUBSCRIPTION_BILLING_INTERVAL.MONTH);
  const [invoices, setInvoices] = useState<SubscriptionInvoiceItem[]>([]);
  const [error, setError] = useState('');
  const [compareOpen, setCompareOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const returnedFromStripeRef = useRef(false);
  const showPlayStoreBillingNotice = isPlayStoreClient();
  const { frontendUrl, playStoreShowOfficialWebsiteUrl } = usePublicConfig(
    showPlayStoreBillingNotice,
  );

  useEffect(() => {
    if (returnedFromStripeRef.current) {
      return;
    }
    const checkoutResult = new URLSearchParams(window.location.search).get('checkout');
    returnedFromStripeRef.current = detectReturnedFromStripe(checkoutResult);
  }, []);

  const loadData = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError('');
    const statusRes = await subscriptionApi.getStatus();
    if (!statusRes.success || !statusRes.data) {
      setError(statusRes.error ?? t('subscription.loadError'));
      setLoading(false);
      return;
    }
    setStatus(statusRes.data);

    if (statusRes.data.stripeConfigured) {
      const previewRes = await subscriptionApi.getPreview();
      if (previewRes.success && previewRes.data) {
        setPreview(previewRes.data);
      } else if (!previewRes.success) {
        setError(previewRes.error ?? t('subscription.loadError'));
      }
      const invoiceRes = await subscriptionApi.listInvoices();
      if (invoiceRes.success && invoiceRes.data) {
        setInvoices(invoiceRes.data.invoices);
      }
    }
    setLoading(false);
  }, [t]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    const checkoutResult = searchParams.get('checkout');
    if (!checkoutResult) {
      return;
    }
    if (checkoutResult === 'success') {
      showSuccessToast(t('subscription.checkoutSuccess'));
      void refreshUser();
      void loadData();
    } else if (checkoutResult === 'cancel') {
      showErrorToast(t('subscription.checkoutCancel'));
    }
    setSearchParams({}, { replace: true });
  }, [loadData, refreshUser, searchParams, setSearchParams, showErrorToast, showSuccessToast, t]);

  const handleBack = (): void => {
    if (returnedFromStripeRef.current) {
      navigate('/', { replace: true });
      return;
    }
    navigateBack(navigate, '/settings');
  };

  const handleSubscribe = async (): Promise<void> => {
    setCheckoutBusy(true);
    const res = await subscriptionApi.createCheckout(selectedInterval);
    setCheckoutBusy(false);
    if (!res.success || !res.data?.checkoutUrl) {
      showErrorToast(res.error ?? t('subscription.loadError'));
      return;
    }
    markStripeCheckoutPending();
    window.location.href = res.data.checkoutUrl;
  };

  const handleManageSubscription = async (): Promise<void> => {
    setPortalBusy(true);
    const res = await subscriptionApi.createPortalSession();
    setPortalBusy(false);
    if (!res.success || !res.data?.portalUrl) {
      showErrorToast(res.error ?? t('subscription.loadError'));
      return;
    }
    markStripeCheckoutPending();
    window.location.href = res.data.portalUrl;
  };

  const handleScheduleIntervalChange = async (
    target: SubscriptionBillingInterval,
  ): Promise<void> => {
    setIntervalBusy(true);
    const res = await subscriptionApi.scheduleIntervalChange(target);
    setIntervalBusy(false);
    if (!res.success) {
      showErrorToast(res.error ?? t('subscription.loadError'));
      return;
    }
    setPendingIntervalConfirm(null);
    showSuccessToast(t('subscription.intervalChangeScheduled'));
    void loadData();
  };

  const handleConfirmIntervalChange = (): void => {
    if (!pendingIntervalConfirm) {
      return;
    }
    void handleScheduleIntervalChange(pendingIntervalConfirm);
  };

  const handleCancelIntervalChange = async (): Promise<void> => {
    setIntervalBusy(true);
    const res = await subscriptionApi.cancelIntervalChange();
    setIntervalBusy(false);
    if (!res.success) {
      showErrorToast(res.error ?? t('subscription.loadError'));
      return;
    }
    setCancelIntervalConfirmOpen(false);
    showSuccessToast(t('subscription.intervalChangeCancelled'));
    void loadData();
  };

  const subscriptionStatusLabel = useMemo(() => {
    if (!status?.subscriptionStatus) {
      return null;
    }
    if (status.subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE) {
      return t('subscription.statusActive');
    }
    if (status.subscriptionStatus === SUBSCRIPTION_STATUS.PAST_DUE) {
      return t('subscription.statusPastDue');
    }
    if (status.subscriptionStatus === SUBSCRIPTION_STATUS.CANCELED) {
      return t('subscription.statusCanceled');
    }
    return status.subscriptionStatus;
  }, [status?.subscriptionStatus, t]);

  const canSubscribe = preview?.canSubscribe === true && status?.stripeConfigured === true;
  const canManageSubscription = status?.hasStripeSubscription === true;
  const showStripeBillingUi = canUseStripeBillingUi();
  const officialFrontendUrl = frontendUrl || window.location.origin;
  const playStoreBillingNoticeText = playStoreShowOfficialWebsiteUrl
    ? `${t('subscription.playStoreBillingNotice')}\n${t('subscription.playStoreOfficialWebsiteLine', {
        url: officialFrontendUrl,
      })}`
    : t('subscription.playStoreBillingNotice');

  const userTimezone = user?.settings?.timezone;
  const locale = mapLanguageToLocale(language);
  const selectedPlan: SubscriptionPlanQuote | null = preview?.plans?.[selectedInterval] ?? null;
  const isChargeToday = selectedPlan?.chargeToday === true;
  const upgradeCopy = useMemo(
    () => resolveSubscriptionUpgradeCopy(preview?.upgradeCopySetting, language),
    [language, preview?.upgradeCopySetting],
  );
  const selectedPeriodLabel =
    selectedInterval === SUBSCRIPTION_BILLING_INTERVAL.YEAR
      ? t('subscription.periodPerYear')
      : t('subscription.periodPerMonth');
  const renderUpgradeTemplate = (
    template: string,
    vars: Record<string, string>,
  ): string =>
    applySubscriptionUpgradeTemplate(
      ensureUpgradeCopyPeriodPlaceholder(template, selectedInterval),
      {
        period: selectedPeriodLabel,
        ...vars,
      },
    );
  const renderPriceAndPromoLines = (
    promo: SubscriptionAppliedPromo | null,
    dueAmount: { currency: string; amount: number },
    listPrice: { currency: string; amount: number },
  ) => {
    if (promo) {
      return (
        <>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">
            {promo.promoType === 'limited_time'
              ? renderUpgradeTemplate(upgradeCopy.promoLimitedTimePrice, {
                  dueAmount: formatMoney(dueAmount.currency, dueAmount.amount),
                  listPrice: formatMoney(listPrice.currency, listPrice.amount),
                  endsAt: formatUtcForUserTimezone(promo.endsAt, userTimezone, locale),
                })
              : renderUpgradeTemplate(upgradeCopy.promoFirstMonthPrice, {
                  dueAmount: formatMoney(dueAmount.currency, dueAmount.amount),
                  listPrice: formatMoney(listPrice.currency, listPrice.amount),
                })}
          </p>
          <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300" role="status">
            {promo.promoType === 'limited_time'
              ? renderUpgradeTemplate(upgradeCopy.promoLimitedTimeBadge, {
                  amount: formatMoney(listPrice.currency, promo.amountOff),
                  endsAt: formatUtcForUserTimezone(promo.endsAt, userTimezone, locale),
                })
              : renderUpgradeTemplate(upgradeCopy.promoFirstMonthBadge, {
                  amount: formatMoney(listPrice.currency, promo.amountOff),
                })}
          </p>
        </>
      );
    }
    return (
      <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">
        {applySubscriptionUpgradeAmountTemplate(
          ensureUpgradeCopyPeriodPlaceholder(upgradeCopy.payTodayAmount, selectedInterval),
          formatMoney(dueAmount.currency, dueAmount.amount),
          selectedPeriodLabel,
        )}
      </p>
    );
  };
  const billingIntervalLabel = (interval: SubscriptionBillingInterval | null | undefined): string => {
    if (interval === SUBSCRIPTION_BILLING_INTERVAL.YEAR) {
      return t('subscription.intervalYear');
    }
    return t('subscription.intervalMonth');
  };

  const intervalChangeConfirmDetails = useMemo(() => {
    if (!pendingIntervalConfirm || !status?.subscriptionEndsAt) {
      return null;
    }
    const targetPlan = preview?.plans[pendingIntervalConfirm];
    if (!targetPlan?.available) {
      return null;
    }
    const periodStartUtc = status.subscriptionEndsAt;
    const periodStartDate = parseUtcDatetimeToDate(periodStartUtc);
    const periodEndUtc = formatUtcDatetimeFromDate(
      addBillingPeriodUtc(periodStartDate, pendingIntervalConfirm),
    );
    const currency = targetPlan.listPriceAmount.currency;
    const listPrice = targetPlan.listPriceAmount.amount;
    const amountDue =
      targetPlan.amountDueToday.amount > 0
        ? targetPlan.amountDueToday.amount
        : listPrice;
    return {
      targetInterval: pendingIntervalConfirm,
      targetLabel: billingIntervalLabel(pendingIntervalConfirm),
      currentLabel: billingIntervalLabel(status.billingInterval),
      effectiveAtText: formatUtcForUserTimezone(periodStartUtc, userTimezone, locale),
      periodStartText: formatUtcForUserTimezone(periodStartUtc, userTimezone, locale),
      periodEndText: formatUtcForUserTimezone(periodEndUtc, userTimezone, locale),
      listPriceText: formatMoney(currency, listPrice),
      amountDueText: formatMoney(currency, amountDue),
      appliedPromo: targetPlan.appliedPromo,
      promoEndsAtText: targetPlan.appliedPromo?.endsAt
        ? formatUtcForUserTimezone(targetPlan.appliedPromo.endsAt, userTimezone, locale)
        : null,
      amountOffText: targetPlan.appliedPromo
        ? formatMoney(currency, targetPlan.appliedPromo.amountOff)
        : null,
    };
  }, [
    pendingIntervalConfirm,
    preview?.plans,
    status?.billingInterval,
    status?.subscriptionEndsAt,
    t,
    userTimezone,
    locale,
  ]);
  const subscriptionEndsAtText = formatUtcForUserTimezone(status?.subscriptionEndsAt, userTimezone, locale);
  const isSubscriptionCancelled =
    status?.cancelAtPeriodEnd === true || status?.subscriptionStatus === SUBSCRIPTION_STATUS.CANCELED;
  const shouldShowCurrentSubscriptionStatus = status?.level === USER_LEVEL.PAID;
  const showPreviousSubscriptionCard =
    status?.level === USER_LEVEL.FREE &&
    status.hasStripeSubscription === true &&
    Boolean(status.subscriptionEndsAt);
  const previousSubscriptionSummary = useMemo(() => {
    if (!showPreviousSubscriptionCard) {
      return null;
    }
    if (status?.subscriptionStatus === SUBSCRIPTION_STATUS.PAST_DUE) {
      return t('subscription.previousSubscriptionPaymentFailed');
    }
    return t('subscription.previousSubscriptionEnded');
  }, [showPreviousSubscriptionCard, status?.subscriptionStatus, t]);
  const subscriptionRenewalHints = useMemo(() => {
    if (!status?.subscriptionEndsAt) {
      return null;
    }
    if (status.level !== USER_LEVEL.PAID) {
      return null;
    }
    if (isSubscriptionCancelled) {
      return {
        main: t('subscription.willEndOnCancelled', { date: subscriptionEndsAtText }),
        limitedTime: null,
      };
    }
    if (status.subscriptionStatus !== SUBSCRIPTION_STATUS.ACTIVE) {
      return null;
    }
    const charge = status.nextRenewalCharge;
    if (!charge) {
      return {
        main: t('subscription.willAutoRenewOn', { date: subscriptionEndsAtText }),
        limitedTime: null,
      };
    }
    const amountText = formatMoneyCompact(charge.currency, charge.amountDue);
    const listPriceText = formatMoneyCompact(charge.currency, charge.listPriceAmount);
    const renewPeriodLabel =
      charge.billingInterval === SUBSCRIPTION_BILLING_INTERVAL.YEAR
        ? t('subscription.periodPerYear')
        : t('subscription.periodPerMonth');
    const main = charge.appliedPromo
      ? t('subscription.willAutoRenewOnPromo', {
          date: subscriptionEndsAtText,
          amount: amountText,
        })
      : t('subscription.willAutoRenewOnWithAmount', {
          date: subscriptionEndsAtText,
          amount: amountText,
        });
    const limitedTime =
      charge.appliedPromo?.promoType === 'limited_time'
        ? t('subscription.nextRenewLimitedTimePromo', {
            dueAmount: formatMoneyCompact(charge.currency, charge.amountDue),
            endsAt: formatUtcForUserTimezone(charge.appliedPromo.endsAt, userTimezone, locale),
            listPrice: listPriceText,
            period: renewPeriodLabel,
          })
        : null;
    return { main, limitedTime };
  }, [
    isSubscriptionCancelled,
    locale,
    status?.level,
    status?.nextRenewalCharge,
    status?.subscriptionEndsAt,
    status?.subscriptionStatus,
    subscriptionEndsAtText,
    t,
    userTimezone,
  ]);

  const showL1TrialCard = status?.level === USER_LEVEL.TRIAL && Boolean(status.trialEndsAt);
  // Invitation L3 trial card: only while on invite-code L3 trial (not after paid Stripe subscription)
  const showInvitationL3TrialCard =
    status?.level === USER_LEVEL.PAID &&
    Boolean(status.invitationLevelEndsAt) &&
    status.hasStripeSubscription !== true;
  const showResubscribeButton = status?.level === USER_LEVEL.FREE && status?.hasStripeSubscription === true;
  const showSubscriptionEndsAtCard = status?.level === USER_LEVEL.PAID && Boolean(status?.subscriptionEndsAt);
  const subscribeActionLabel = !isChargeToday
    ? t('subscription.startTrialButton')
    : showResubscribeButton
      ? t('subscription.resubscribeButton')
      : t('subscription.subscribeButton');
  const subscribeConsentParts = buildSubscriptionTermsConsentParts(
    isChargeToday
      ? t('subscription.termsConsentPay')
      : t('subscription.termsConsentSetup'),
    subscribeActionLabel,
    t('subscription.termsLinkLabel'),
  );
  const handleOpenSubscriptionTerms = (): void => {
    setTermsOpen(true);
  };
  const handleCloseSubscriptionTerms = (): void => {
    setTermsOpen(false);
  };

  return (
    <div className="min-h-full bg-gray-50 pb-8 dark:bg-gray-950">
      <header className="z-10 bg-white shadow-sm dark:bg-gray-900 dark:shadow-black/20">
        <div className="mx-auto max-w-lg px-4 py-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleBack}
              aria-label={t('common.back')}
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {t('subscription.title')}
            </h1>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-6">
      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
      ) : null}

      {!loading && error ? (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && status ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3 rounded-lg border border-primary-200 bg-primary-50 px-3 py-3 dark:border-primary-900 dark:bg-primary-950/30">
            <div className="min-w-0">
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('subscription.membershipLevel')}</p>
              <p className="mt-0.5 text-sm font-semibold text-primary-800 dark:text-primary-200">
                {t(levelLabelKey(status.level))}
              </p>
              {shouldShowCurrentSubscriptionStatus && subscriptionStatusLabel ? (
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">{subscriptionStatusLabel}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => setCompareOpen(true)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setCompareOpen(true);
                }
              }}
              tabIndex={0}
              aria-label={t('usage.compareLevels')}
              className="shrink-0 rounded-lg border border-primary-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-primary-700 shadow-sm transition hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-primary-800 dark:bg-gray-900 dark:text-primary-300 dark:hover:bg-primary-950/40"
            >
              {t('usage.compareLevels')}
            </button>
          </div>

          {showPreviousSubscriptionCard ? (
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-3 dark:border-gray-700 dark:bg-gray-900">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('subscription.previousSubscriptionTitle')}
              </p>
              <p className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                {subscriptionEndsAtText}
              </p>
              {previousSubscriptionSummary ? (
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                  {previousSubscriptionSummary}
                </p>
              ) : null}
            </div>
          ) : null}

          {showL1TrialCard ? (
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-3 dark:border-gray-700 dark:bg-gray-900">
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('subscription.trialEndsAt')}</p>
              <p className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                {formatUtcForUserTimezone(status.trialEndsAt, userTimezone, locale)}
              </p>
            </div>
          ) : null}

          {showInvitationL3TrialCard ? (
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-3 dark:border-gray-700 dark:bg-gray-900">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('subscription.invitationTrialEndsAt')}
              </p>
              <p className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                {formatUtcForUserTimezone(status.invitationLevelEndsAt, userTimezone, locale)}
              </p>
            </div>
          ) : null}

          {showSubscriptionEndsAtCard ? (
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-3 dark:border-gray-700 dark:bg-gray-900">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('subscription.subscriptionEndsAt')}
              </p>
              <p className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                {subscriptionEndsAtText}
              </p>
              {status.billingInterval ? (
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                  {t('subscription.currentBillingInterval', {
                    interval: billingIntervalLabel(status.billingInterval),
                  })}
                </p>
              ) : null}
              {subscriptionRenewalHints ? (
                <>
                  <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                    {subscriptionRenewalHints.main}
                  </p>
                  {subscriptionRenewalHints.limitedTime ? (
                    <p
                      className="mt-1 text-xs text-emerald-700 dark:text-emerald-300"
                      role="status"
                    >
                      {subscriptionRenewalHints.limitedTime}
                    </p>
                  ) : null}
                </>
              ) : null}
              {status.pendingBillingInterval && status.intervalChangeAt ? (
                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-2 dark:border-amber-900 dark:bg-amber-950/40">
                  <p className="text-xs text-amber-800 dark:text-amber-200" role="status">
                    {t('subscription.pendingIntervalChange', {
                      interval: billingIntervalLabel(status.pendingBillingInterval),
                      date: formatUtcForUserTimezone(
                        status.intervalChangeAt,
                        userTimezone,
                        locale,
                      ),
                    })}
                  </p>
                  {status.lockedPromo ? (
                    <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
                      {t('subscription.lockedPromoReserved', {
                        amount: formatMoneyCompact(
                          status.nextRenewalCharge?.currency ?? 'HKD',
                          status.lockedPromo.amountOff,
                        ),
                      })}
                    </p>
                  ) : null}
                  {showStripeBillingUi ? (
                    <button
                      type="button"
                      className="mt-2 text-xs font-semibold text-amber-900 underline dark:text-amber-100"
                      disabled={intervalBusy}
                      aria-label={t('subscription.cancelIntervalChange')}
                      onClick={() => {
                        setCancelIntervalConfirmOpen(true);
                      }}
                    >
                      {intervalBusy ? t('common.loading') : t('subscription.cancelIntervalChange')}
                    </button>
                  ) : null}
                </div>
              ) : null}
              {showStripeBillingUi &&
              !isSubscriptionCancelled &&
              status.subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE &&
              status.billingInterval &&
              !status.pendingBillingInterval ? (
                <div className="mt-3 flex flex-col gap-2">
                  {status.billingInterval === SUBSCRIPTION_BILLING_INTERVAL.MONTH &&
                  preview?.plans.year.available ? (
                    <button
                      type="button"
                      className="rounded-md border border-primary-300 px-3 py-2 text-xs font-semibold text-primary-700 hover:bg-primary-50 disabled:opacity-60 dark:border-primary-700 dark:text-primary-300 dark:hover:bg-primary-900/20"
                      disabled={intervalBusy}
                      aria-label={t('subscription.switchToYear')}
                      onClick={() => {
                        setPendingIntervalConfirm(SUBSCRIPTION_BILLING_INTERVAL.YEAR);
                      }}
                    >
                      {intervalBusy ? t('common.loading') : t('subscription.switchToYear')}
                    </button>
                  ) : null}
                  {status.billingInterval === SUBSCRIPTION_BILLING_INTERVAL.YEAR ? (
                    <button
                      type="button"
                      className="rounded-md border border-primary-300 px-3 py-2 text-xs font-semibold text-primary-700 hover:bg-primary-50 disabled:opacity-60 dark:border-primary-700 dark:text-primary-300 dark:hover:bg-primary-900/20"
                      disabled={intervalBusy}
                      aria-label={t('subscription.switchToMonth')}
                      onClick={() => {
                        setPendingIntervalConfirm(SUBSCRIPTION_BILLING_INTERVAL.MONTH);
                      }}
                    >
                      {intervalBusy ? t('common.loading') : t('subscription.switchToMonth')}
                    </button>
                  ) : null}
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('subscription.intervalChangeNote')}
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}

          {showPlayStoreBillingNotice ? (
            <p
              className="whitespace-pre-line rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
              role="status"
            >
              {playStoreBillingNoticeText}
            </p>
          ) : null}

          {!status.stripeConfigured ? (
            <p className="text-sm text-amber-700 dark:text-amber-300" role="status">
              {t('subscription.stripeNotConfigured')}
            </p>
          ) : null}

          {preview && canSubscribe && selectedPlan && showStripeBillingUi ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/60">
              <div
                className="mb-3 grid grid-cols-2 gap-2"
                role="group"
                aria-label={t('subscription.selectBillingInterval')}
              >
                {([SUBSCRIPTION_BILLING_INTERVAL.MONTH, SUBSCRIPTION_BILLING_INTERVAL.YEAR] as const).map(
                  (interval) => {
                    const plan = preview.plans[interval];
                    const selected = selectedInterval === interval;
                    const disabled = !plan.available;
                    return (
                      <button
                        key={interval}
                        type="button"
                        disabled={disabled}
                        aria-pressed={selected}
                        aria-label={billingIntervalLabel(interval)}
                        className={
                          selected
                            ? 'rounded-lg border border-primary-500 bg-primary-50 px-3 py-2 text-sm font-semibold text-primary-800 dark:border-primary-400 dark:bg-primary-950/40 dark:text-primary-100'
                            : 'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'
                        }
                        onClick={() => {
                          setSelectedInterval(interval);
                        }}
                      >
                        {billingIntervalLabel(interval)}
                      </button>
                    );
                  },
                )}
              </div>
              {!selectedPlan.available ? (
                <p className="text-sm text-amber-700 dark:text-amber-300" role="status">
                  {t('subscription.planUnavailable')}
                </p>
              ) : (
                <>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {isChargeToday ? upgradeCopy.payTodayTitle : upgradeCopy.deferredTitle}
                  </h2>
                  {isChargeToday
                    ? renderPriceAndPromoLines(
                        selectedPlan.appliedPromo,
                        selectedPlan.amountDueToday,
                        selectedPlan.listPriceAmount,
                      )
                    : renderPriceAndPromoLines(
                        selectedPlan.appliedPromo,
                        selectedPlan.firstChargeAmount,
                        selectedPlan.listPriceAmount,
                      )}
                  {!isChargeToday ? (
                    <>
                      <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">
                        {upgradeCopy.deferredNoChargeToday}
                      </p>
                      <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">
                        {renderUpgradeTemplate(upgradeCopy.deferredFirstChargeOn, {
                          date: formatUtcForUserTimezone(
                            selectedPlan.nextChargeAt,
                            userTimezone,
                            locale,
                          ),
                        })}
                      </p>
                      <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">
                        {t('subscription.firstChargeAmount', {
                          amount: formatMoney(
                            selectedPlan.firstChargeAmount.currency,
                            selectedPlan.firstChargeAmount.amount,
                          ),
                        })}
                      </p>
                      <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">
                        {renderUpgradeTemplate(upgradeCopy.deferredCoversPeriod, {
                          start: formatUtcForUserTimezone(
                            selectedPlan.firstPaidPeriodStart,
                            userTimezone,
                            locale,
                          ),
                          end: formatUtcForUserTimezone(
                            selectedPlan.firstPaidPeriodEnd,
                            userTimezone,
                            locale,
                          ),
                        })}
                      </p>
                    </>
                  ) : null}
                  {!isChargeToday &&
                  preview.invitationLevelEndsAt &&
                  preview.invitationLevelEndsAt > preview.subscribeAt ? (
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                      {renderUpgradeTemplate(upgradeCopy.deferredInvitationTrialNote, {
                        date: formatUtcForUserTimezone(
                          preview.invitationLevelEndsAt,
                          userTimezone,
                          locale,
                        ),
                      })}
                    </p>
                  ) : null}
                  {!isChargeToday && selectedPlan.appliedPromo != null ? (
                    <p className="mt-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                      {t('subscription.stripeCheckoutDiscountHiddenNote')}
                    </p>
                  ) : null}
                  {!isChargeToday && selectedPlan.promoMayExpireIfDeferred ? (
                    <p
                      className="mt-2 text-xs leading-relaxed text-amber-800 dark:text-amber-200"
                      role="status"
                    >
                      {t('subscription.promoMayExpireIfDeferred')}
                    </p>
                  ) : null}
                  <AppButton
                    type="button"
                    variant="primary"
                    fullWidth
                    className="mt-4"
                    onClick={() => {
                      void handleSubscribe();
                    }}
                    disabled={checkoutBusy || !selectedPlan.available}
                    aria-label={subscribeActionLabel}
                  >
                    {checkoutBusy ? t('common.loading') : subscribeActionLabel}
                  </AppButton>
                  <p className="mt-3 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                    {subscribeConsentParts.before}
                    <button
                      type="button"
                      onClick={handleOpenSubscriptionTerms}
                      className="font-medium text-primary-700 underline underline-offset-2 hover:text-primary-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-primary-300 dark:hover:text-primary-200"
                      aria-label={t('subscription.termsLinkLabel')}
                      tabIndex={0}
                    >
                      {subscribeConsentParts.termsLinkLabel}
                    </button>
                    {subscribeConsentParts.after}
                  </p>
                </>
              )}
            </div>
          ) : null}

          {preview && !preview.canSubscribe ? (
            <p className="text-sm text-gray-600 dark:text-gray-300">{t('subscription.alreadySubscribed')}</p>
          ) : null}

          {status.stripeConfigured && canManageSubscription ? (
            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
              {showStripeBillingUi ? (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => {
                      void handleManageSubscription();
                    }}
                    disabled={portalBusy}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100 disabled:opacity-60 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-800"
                    aria-label={t('subscription.manageButton')}
                  >
                    {portalBusy ? t('common.loading') : t('subscription.manageButton')}
                  </button>
                </div>
              ) : null}
              {invoices.length > 0 ? (
                <div
                  className={
                    showStripeBillingUi
                      ? 'mt-3 border-t border-gray-200 pt-3 dark:border-gray-700'
                      : ''
                  }
                >
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                    {t('subscription.invoicesTitle')}
                  </p>
                  <div className="mt-2 space-y-2">
                    {invoices.slice(0, 5).map((invoice) => (
                      <div
                        key={invoice.id}
                        className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 dark:border-gray-700"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {formatMoney(invoice.currency, invoice.amountPaid > 0 ? invoice.amountPaid : invoice.amountDue)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {`${formatUtcForUserTimezone(invoice.createdAt, userTimezone, locale)} · ${invoice.status}`}
                          </p>
                        </div>
                        {invoice.hostedInvoiceUrl ? (
                          <a
                            href={invoice.hostedInvoiceUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-md border border-primary-300 px-3 py-1.5 text-xs font-semibold text-primary-700 hover:bg-primary-50 dark:border-primary-700 dark:text-primary-300 dark:hover:bg-primary-900/20"
                          >
                            {t('subscription.viewInvoiceButton')}
                          </a>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">-</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              <div
                className={
                  showStripeBillingUi || invoices.length > 0
                    ? 'mt-3 border-t border-gray-200 pt-3 dark:border-gray-700'
                    : ''
                }
              >
                <button
                  type="button"
                  onClick={handleOpenSubscriptionTerms}
                  className="text-xs font-medium text-primary-700 underline underline-offset-2 hover:text-primary-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-primary-300 dark:hover:text-primary-200"
                  aria-label={t('subscription.termsLinkLabel')}
                  tabIndex={0}
                >
                  {t('subscription.termsLinkLabel')}
                </button>
              </div>
            </div>
          ) : null}

          <RedeemInvitationCodeForm
            variant="subscription"
            onRedeemed={() => {
              void refreshUser();
              void loadData();
            }}
          />
        </div>
      ) : null}
      </main>

      <ConfirmDialog
        open={pendingIntervalConfirm != null && intervalChangeConfirmDetails != null}
        title={
          pendingIntervalConfirm === SUBSCRIPTION_BILLING_INTERVAL.YEAR
            ? t('subscription.intervalChangeConfirmTitleYear')
            : t('subscription.intervalChangeConfirmTitleMonth')
        }
        description={t('subscription.intervalChangeConfirmIntro', {
          current: intervalChangeConfirmDetails?.currentLabel ?? '',
          target: intervalChangeConfirmDetails?.targetLabel ?? '',
        })}
        confirmLabel={
          intervalBusy
            ? t('common.loading')
            : pendingIntervalConfirm === SUBSCRIPTION_BILLING_INTERVAL.YEAR
              ? t('subscription.intervalChangeConfirmButtonYear')
              : t('subscription.intervalChangeConfirmButtonMonth')
        }
        cancelLabel={t('common.cancel')}
        onConfirm={handleConfirmIntervalChange}
        onCancel={() => {
          if (intervalBusy) {
            return;
          }
          setPendingIntervalConfirm(null);
        }}
        confirmDisabled={intervalBusy || !intervalChangeConfirmDetails}
        cancelDisabled={intervalBusy}
        variant="default"
      >
        {intervalChangeConfirmDetails ? (
          <dl className="mt-4 space-y-3 rounded-lg border border-gray-700 bg-gray-950/60 p-3 text-sm">
            <div>
              <dt className="text-xs text-gray-400">
                {t('subscription.intervalChangeConfirmEffectiveAt')}
              </dt>
              <dd className="mt-0.5 font-medium text-gray-100">
                {intervalChangeConfirmDetails.effectiveAtText}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-400">
                {t('subscription.intervalChangeConfirmFirstChargeAt')}
              </dt>
              <dd className="mt-0.5 font-medium text-gray-100">
                {intervalChangeConfirmDetails.effectiveAtText}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-400">
                {t('subscription.intervalChangeConfirmCoversPeriod')}
              </dt>
              <dd className="mt-0.5 font-medium text-gray-100">
                {t('subscription.intervalChangeConfirmCoversPeriodValue', {
                  start: intervalChangeConfirmDetails.periodStartText,
                  end: intervalChangeConfirmDetails.periodEndText,
                })}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-400">
                {t('subscription.intervalChangeConfirmAmount')}
              </dt>
              <dd className="mt-0.5 font-medium text-gray-100">
                {intervalChangeConfirmDetails.appliedPromo ? (
                  <>
                    <span className="text-emerald-300">
                      {intervalChangeConfirmDetails.amountDueText}
                    </span>
                    <span className="ml-2 text-xs text-gray-400 line-through">
                      {intervalChangeConfirmDetails.listPriceText}
                    </span>
                  </>
                ) : (
                  intervalChangeConfirmDetails.amountDueText
                )}
              </dd>
              {intervalChangeConfirmDetails.appliedPromo &&
              intervalChangeConfirmDetails.amountOffText ? (
                <dd className="mt-1 text-xs text-emerald-300" role="status">
                  {intervalChangeConfirmDetails.appliedPromo.promoType === 'limited_time'
                    ? t('subscription.intervalChangeConfirmPromoLimited', {
                        amount: intervalChangeConfirmDetails.amountOffText,
                        endsAt: intervalChangeConfirmDetails.promoEndsAtText ?? '',
                      })
                    : t('subscription.intervalChangeConfirmPromoGeneric', {
                        amount: intervalChangeConfirmDetails.amountOffText,
                      })}
                </dd>
              ) : null}
            </div>
            <div>
              <dt className="sr-only">{t('subscription.intervalChangeConfirmNotes')}</dt>
              <dd className="space-y-1 text-xs text-gray-400">
                <p>{t('subscription.intervalChangeConfirmNoRefund')}</p>
                <p>{t('subscription.intervalChangeConfirmPromoLockNote')}</p>
              </dd>
            </div>
          </dl>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={
          cancelIntervalConfirmOpen &&
          status?.pendingBillingInterval != null &&
          status.intervalChangeAt != null
        }
        title={t('subscription.cancelIntervalChangeConfirmTitle')}
        description={t('subscription.cancelIntervalChangeConfirmIntro', {
          interval: billingIntervalLabel(status?.pendingBillingInterval),
          date: formatUtcForUserTimezone(status?.intervalChangeAt, userTimezone, locale),
        })}
        confirmLabel={
          intervalBusy
            ? t('common.loading')
            : t('subscription.cancelIntervalChangeConfirmButton')
        }
        cancelLabel={t('common.cancel')}
        onConfirm={() => {
          void handleCancelIntervalChange();
        }}
        onCancel={() => {
          if (intervalBusy) {
            return;
          }
          setCancelIntervalConfirmOpen(false);
        }}
        confirmDisabled={intervalBusy}
        cancelDisabled={intervalBusy}
        variant="danger"
      >
        {status?.lockedPromo ? (
          <p className="mt-3 text-xs text-gray-400" role="status">
            {t('subscription.cancelIntervalChangeConfirmPromoNote', {
              amount: formatMoneyCompact(
                status.nextRenewalCharge?.currency ?? 'HKD',
                status.lockedPromo.amountOff,
              ),
            })}
          </p>
        ) : null}
      </ConfirmDialog>

      <MembershipPricingCompareModal open={compareOpen} onClose={() => setCompareOpen(false)} />
      <SubscriptionTermsModal open={termsOpen} onClose={handleCloseSubscriptionTerms} />
    </div>
  );
};
